import {
  Device,
  PaybackTrackingData,
  SARData,
  StepData,
  TrackingDataMap,
  TrackingMetaData,
  TrackingStep,
} from '../types/trackingDataModel';
import { TrackingHandlerBase } from '../types/handlers';
import { resolveStepType } from '../trackingUtils';
import {
  LOGIN_SESSION_STORAGE_KEY,
  TRACKING_STEP_TYPES,
} from '../constants/constants';
import { loadSessionData } from '../../../../utils/sessionStorageUtils';
import { FormValues } from '../../../contexte/prefillContext';

export class PaybackTrackingHandler extends TrackingHandlerBase<PaybackTrackingData> {
  // resolved once and cached so it doesn't change mid-session
  private hasLoginSession: boolean | null = null;

  private getAsbSessionDefaults(): FormValues {
    return loadSessionData<FormValues>(LOGIN_SESSION_STORAGE_KEY)
      ?.asbFormDefaultValues;
  }

  updateDeviceInfo = (
    deviceInfo: Partial<Device>,
    data: TrackingDataMap['payback']
  ): Partial<PaybackTrackingData> => {
    return {
      device: {
        ...data.device,
        ...deviceInfo,
        pbAppVersion: null, // no pbAppVersion provided
      },
    };
  };

  updateTrackingMetadata = (
    trackingMetadata: TrackingMetaData,
    data: PaybackTrackingData
  ): Partial<PaybackTrackingData> => {
    const loginSession = loadSessionData(LOGIN_SESSION_STORAGE_KEY);
    const resolvedAppId =
      loginSession?.trackingSessionId || trackingMetadata.trackingId;
    return {
      appId: data.appId || resolvedAppId,
      productName: trackingMetadata.project || data.productName,
    };
  };

  updateSARConsentInfo = (sarData: SARData): Partial<PaybackTrackingData> => {
    return {
      isSaR: sarData.consent,
    };
  };

  updateFromQueryParams = (params: string): Partial<PaybackTrackingData> => {
    const searchParams = new URLSearchParams(params);
    return {
      queryParams: searchParams.toString(),
      sourceCode:
        searchParams.get('source_code') ||
        this.getAsbSessionDefaults()?.source_code ||
        '',
      salesCode:
        searchParams.get('sales_code') ||
        this.getAsbSessionDefaults()?.sales_code ||
        '',
    };
  };

  updateOnStepChange = (
    step: StepData,
    data: PaybackTrackingData
  ): Partial<PaybackTrackingData> => {
    const currentSteps = data.steps ?? []; // preserve old steps
    const lastStep = currentSteps[currentSteps.length - 1];
    if (this.hasLoginSession === null) {
      this.hasLoginSession = Boolean(
        loadSessionData<{ hasLoginStep?: boolean }>(LOGIN_SESSION_STORAGE_KEY)
          ?.hasLoginStep
      );
    }
    const hasLoginSession = this.hasLoginSession;
    const indexOffset = hasLoginSession ? 1 : 0;
    const adjustedIndex = step.index + indexOffset;
    const resolvedStepType = resolveStepType(step, currentSteps);
    // Login page is the Start and first form page is Next when login session exists
    const stepType =
      hasLoginSession && resolvedStepType === TRACKING_STEP_TYPES.START
        ? TRACKING_STEP_TYPES.NEXT
        : resolvedStepType;

    if (
      !stepType ||
      (lastStep &&
        lastStep.index === adjustedIndex &&
        lastStep.stepType === stepType)
    )
      return data;

    const newStep: TrackingStep = {
      index: adjustedIndex,
      name: step.name,
      timestamp: step.visitedOn,
      stepType,
    };

    return {
      steps: [...currentSteps, newStep],
      lastStepIndex: adjustedIndex,
    };
  };

  updateOnLogin = (
    payload: { name: string; timestamp: string },
    data: PaybackTrackingData
  ): Partial<PaybackTrackingData> | null => {
    const currentSteps = data.steps ?? [];
    this.hasLoginSession = true;

    if (
      currentSteps.some(
        (step) =>
          step.index === 0 && step.stepType === TRACKING_STEP_TYPES.START
      )
    ) {
      return null;
    }

    const loginStep: TrackingStep = {
      index: 0,
      name: payload.name,
      timestamp: payload.timestamp,
      stepType: TRACKING_STEP_TYPES.START,
    };

    return {
      steps: [loginStep, ...currentSteps],
      lastStepIndex: 0, // API requires 0-100, must not stay at the default -1
    };
  };

  updateOnSuccess = (
    successPageId: string,
    data: PaybackTrackingData
  ): Partial<PaybackTrackingData> => {
    const currentSteps = data.steps ?? [];
    const lastStep = currentSteps[currentSteps.length - 1];

    if (
      lastStep &&
      lastStep.stepType.toString() === TRACKING_STEP_TYPES.SUCCESS_PAGE
    )
      return data; // prevent duplicate step

    const newStep: TrackingStep = {
      index: (lastStep?.index ?? -2) + 1,
      name: successPageId,
      timestamp: new Date().toISOString(),
      stepType: TRACKING_STEP_TYPES.SUCCESS_PAGE,
    };

    return {
      steps: [...currentSteps, newStep],
    };
  };
}
